import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, normalizeUploadFilename } from '../utils/uploadUrl';

function todayDateInputValue() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function toDateInputValue(dateStr) {
  if (!dateStr) return todayDateInputValue();
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return todayDateInputValue();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function AdminNews() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image: '',
    images: [],
    date: todayDateInputValue()
  });
  const [selectedPostComments, setSelectedPostComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPosts();
  }, [navigate]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get('/api/news');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/news/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchPosts();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete post');
    }
  };

  const uploadMediaFile = async (file) => {
    if (!file) return null;

    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 100 * 1024 * 1024 : 5 * 1024 * 1024;

    if (file.size > maxSize) {
      alert(isVideo ? 'Video size must be less than 100MB' : 'Image size must be less than 5MB');
      return null;
    }

    const token = getAccessToken();
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    const response = await axios.post('/api/upload', uploadFormData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    const filename = response.data.filename || normalizeUploadFilename(response.data.path);
    if (!filename) {
      throw new Error('No filename returned from server');
    }

    return {
      filename,
      mediaType: response.data.mediaType || (isVideo ? 'video' : 'image')
    };
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type.startsWith('video/')) {
      alert('Main thumbnail must be an image file.');
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    try {
      const uploaded = await uploadMediaFile(file);
      if (!uploaded) return;

      setFormData((prevFormData) => ({
        ...prevFormData,
        image: uploaded.filename
      }));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload image');
      console.error('Error uploading main image:', error);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleAdditionalMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const uploaded = await uploadMediaFile(file);
      if (!uploaded) return;

      const mediaItem = { filename: uploaded.filename, type: uploaded.mediaType };
      setFormData((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, mediaItem]
      }));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload file');
      console.error('Error uploading media:', error);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData(prevFormData => {
      const newImages = prevFormData.images.filter((_, i) => i !== index);
      const removedItem = prevFormData.images[index];
      const removedFilename = typeof removedItem === 'string' ? removedItem : removedItem.filename;
      return {
        ...prevFormData,
        images: newImages,
        image: newImages.length > 0 && prevFormData.image === removedFilename ? (typeof newImages[0] === 'string' ? newImages[0] : newImages[0].filename) : prevFormData.image
      };
    });
  };

  const fetchComments = async (postId) => {
    setLoadingComments(true);
    try {
      const token = getAccessToken();
      const response = await axios.get(`/api/news/${postId}/comments/all`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setComments(response.data);
      setSelectedPostComments(postId);
    } catch (error) {
      console.error('Error fetching comments:', error);
      alert('Failed to fetch comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/news/comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Refresh comments
      if (selectedPostComments) {
        fetchComments(selectedPostComments);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete comment');
    }
  };

  const handleUpdateCommentStatus = async (commentId, status) => {
    try {
      const token = getAccessToken();
      await axios.patch(
        `/api/news/comments/${commentId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Refresh comments after moderation change
      if (selectedPostComments) {
        fetchComments(selectedPostComments);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update comment status');
    }
  };

  const handleEdit = async (post) => {
    setEditingPost(post);
    // Convert images array to format with type if needed
    const images = (post.images || []).map(img => {
      if (typeof img === 'string') {
        return { filename: img, type: 'image' };
      }
      return img;
    });
    setFormData({
      title: post.title || '',
      content: post.content || '',
      image: normalizeUploadFilename(post.image || ''),
      images: images,
      date: toDateInputValue(post.date)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAccessToken();
      const submitData = {
        ...formData,
        images: formData.images || [] // Ensure images array is sent
      };
      
      if (editingPost) {
        await axios.put(`/api/news/${editingPost.id}`, submitData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        await axios.post('/api/news', submitData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      setShowModal(false);
      setEditingPost(null);
      setFormData({ title: '', content: '', image: '', images: [], date: todayDateInputValue() });
      fetchPosts();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save post');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3 mb-0">News & Posts Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingPost(null);
              setFormData({ title: '', content: '', image: '', images: [], date: todayDateInputValue() });
              setShowModal(true);
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>Create New Post
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Image</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No posts found
                      </td>
                    </tr>
                  ) : (
                    posts.map((post) => (
                      <tr key={post.id}>
                        <td>{post.title}</td>
                        <td>{post.full_name || post.username || '-'}</td>
                        <td>{new Date(post.date).toLocaleDateString()}</td>
                        <td>
                          {post.image ? (
                            <img
                              src={getUploadUrl(post.image)}
                              alt={post.title}
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = '/img/Gallery/opd1.jpg';
                              }}
                            />
                          ) : (
                            <span className="text-muted">No image</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(post)}
                            title="Edit Post"
                          >
                            <i className="bi bi-pencil"></i> Edit
                          </button>
                          <button
                            className="btn btn-sm btn-info me-2"
                            onClick={() => fetchComments(post.id)}
                            title="View Comments"
                          >
                            <i className="bi bi-chat-dots"></i> Comments
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(post.id)}
                            title="Delete Post"
                          >
                            <i className="bi bi-trash"></i> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingPost ? 'Edit Post' : 'Create New Post'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      setEditingPost(null);
                    }}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Publication date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                      />
                      <small className="text-muted">
                        Choose the date shown on the public news page. Defaults to today for new posts.
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Content</label>
                      <textarea
                        className="form-control"
                        rows="10"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        required
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Main Image (for thumbnail)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleMainImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <div className="mt-2">
                          <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Uploading...</span>
                          </div>
                          <span className="text-muted">Uploading image...</span>
                        </div>
                      )}
                      {formData.image && (
                        <div className="mt-3">
                          <img
                            src={getUploadUrl(formData.image)}
                            alt="Preview"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <p className="text-muted mt-2 small">Main image: {formData.image}</p>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Additional Images & Videos (for carousel)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*,video/*"
                        onChange={handleAdditionalMediaUpload}
                        disabled={uploadingImage}
                        multiple={false}
                      />
                      <small className="text-muted d-block mt-1">Upload multiple images or videos one at a time. They will auto-slide in a carousel. (Images: max 5MB, Videos: max 100MB)</small>
                      {formData.images.length > 0 && (
                        <div className="mt-3">
                          <p className="small text-muted mb-2">Uploaded Media ({formData.images.length}):</p>
                          <div className="d-flex flex-wrap gap-2">
                            {formData.images.map((item, index) => {
                              const filename = typeof item === 'string' ? item : item.filename;
                              const mediaType = typeof item === 'string' ? 'image' : (item.type || 'image');
                              return (
                                <div key={index} className="position-relative" style={{ width: '120px', height: '120px' }}>
                                  {mediaType === 'video' ? (
                                    <video
                                      src={getUploadUrl(filename)}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                      muted
                                    />
                                  ) : (
                                    <img
                                      src={getUploadUrl(filename)}
                                      alt={`Media ${index + 1}`}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                  )}
                                  <span className="badge bg-secondary position-absolute top-0 start-0" style={{ margin: '4px', fontSize: '0.7rem' }}>
                                    {mediaType}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                    style={{ margin: '4px' }}
                                    onClick={() => removeImage(index)}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowModal(false);
                        setEditingPost(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingPost ? 'Update' : 'Create'} Post
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Comments Management Modal */}
        {selectedPostComments && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Comments Management</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setSelectedPostComments(null);
                      setComments([]);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingComments ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-muted text-center">No comments found for this post.</p>
                  ) : (
                    <div className="list-group">
                      {comments.map((comment) => (
                        <div key={comment.id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="mb-1">{comment.name}</h6>
                              {comment.email && (
                                <small className="text-muted d-block">{comment.email}</small>
                              )}
                            </div>
                            <div className="text-end">
                              <span className={`badge ${comment.status === 'approved' ? 'bg-success' : comment.status === 'pending' ? 'bg-warning' : 'bg-danger'} me-2`}>
                                {comment.status}
                              </span>
                              <button
                                className="btn btn-sm btn-success me-1"
                                onClick={() => handleUpdateCommentStatus(comment.id, 'approved')}
                                title="Approve Comment"
                                disabled={comment.status === 'approved'}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-sm btn-warning me-1"
                                onClick={() => handleUpdateCommentStatus(comment.id, 'rejected')}
                                title="Reject Comment"
                                disabled={comment.status === 'rejected'}
                              >
                                Reject
                              </button>
                              <button
                                className="btn btn-sm btn-secondary me-1"
                                onClick={() => handleUpdateCommentStatus(comment.id, 'spam')}
                                title="Mark as Spam"
                                disabled={comment.status === 'spam'}
                              >
                                Spam
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteComment(comment.id)}
                                title="Delete Comment"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                          <p className="mb-1">{comment.comment}</p>
                          <small className="text-muted">
                            {new Date(comment.created_at).toLocaleString()}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedPostComments(null);
                      setComments([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminNews;
